import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import EqualizerRoundedIcon from "@mui/icons-material/EqualizerRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";
import Seo from "../../components/Seo";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";

export default function TremoloLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/tremolo";
  const pageTitle = "Tremolo in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what tremolo does, how it modulates volume over time, how it differs from vibrato, and where tremolo is used in music production and guitar tones.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "tremolo",
          "what does tremolo do",
          "tremolo effect",
          "tremolo vs vibrato",
          "guitar tremolo",
          "audio modulation",
          "volume modulation",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Tremolo" },
          { "@type": "Thing", name: "Volume modulation" },
          { "@type": "Thing", name: "Vibrato" },
          { "@type": "Thing", name: "Guitar amplifier tremolo" },
          { "@type": "Thing", name: "Modulation effect" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Tremolo",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Tremolo changes volume over time, creating pulsing, throbbing, choppy, or rhythmic movement without changing the pitch.",
      icon: <EqualizerRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Tremolo adds motion and emotion with a very direct sound, from subtle shimmer to bold rhythmic chopping.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Electric guitar, keyboards, pads, vocals, ambient textures, and creative rhythmic production.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const tremoloTypes = [
    {
      name: "Classic Amp Tremolo",
      description:
        "The vintage pulsing sound built into many classic amplifiers, often warm, musical, and strongly tied to guitar history.",
    },
    {
      name: "Optical Tremolo",
      description:
        "Known for a smooth and musical feel, often associated with classic amp circuits and rounded movement.",
    },
    {
      name: "Bias Tremolo",
      description:
        "A style of amp tremolo with a distinctive feel and a more organic pulsing quality in many vintage designs.",
    },
    {
      name: "Hard Chopper Tremolo",
      description:
        "Uses sharper waveform shapes for more dramatic rhythmic chopping and stutter-like effects.",
    },
    {
      name: "Stereo Tremolo / Auto-Pan",
      description:
        "Moves level or emphasis between left and right channels, creating width and motion across the stereo field.",
    },
    {
      name: "Tempo-Synced Tremolo",
      description:
        "Locks the tremolo speed to musical note divisions so the pulsing lines up with the song tempo.",
    },
  ];

  const tremoloConcepts = [
    {
      name: "Volume Modulation",
      description:
        "Tremolo works by modulating amplitude, meaning it turns the signal up and down over time.",
    },
    {
      name: "Rate",
      description:
        "The speed of the pulsing. Slow rates feel smooth and hypnotic, while fast rates can feel urgent or almost buzzing.",
    },
    {
      name: "Depth",
      description:
        "The intensity of the volume change. Lower depth is subtle, while higher depth can create strong pulsing or chopping.",
    },
    {
      name: "Wave Shape",
      description:
        "The shape of the modulation affects the feel. Smoother shapes sound gentle, while square-like shapes sound more abrupt and rhythmic.",
    },
  ];

  const controls = [
    {
      name: "Rate / Speed",
      description:
        "Controls how quickly the tremolo pulses. This strongly affects whether the feel is dreamy, rhythmic, or intense.",
    },
    {
      name: "Depth / Intensity",
      description:
        "Controls how much the volume moves up and down over time.",
    },
    {
      name: "Wave Shape",
      description:
        "Changes the contour of the modulation, from smooth sine-like pulses to sharper square-like chops.",
    },
    {
      name: "Sync / Note Division",
      description:
        "Lets the tremolo lock to song tempo and musical note values such as quarter notes, eighth notes, or triplets.",
    },
    {
      name: "Stereo Spread / Phase",
      description:
        "On stereo tremolo, controls how the movement is distributed between left and right channels.",
    },
    {
      name: "Mix",
      description:
        "Blends dry and effected sound on designs that support parallel-style control.",
    },
  ];

  const commonUses = [
    "Adding vintage pulsing character to clean guitar",
    "Creating hypnotic motion on chords, pads, and keys",
    "Building rhythmic movement into sustained sounds",
    "Making ambient textures feel alive without using delay or reverb alone",
    "Creating chopped or stutter-like effects in electronic or experimental music",
    "Adding stereo motion with auto-pan style tremolo",
  ];

  const bestPractices = [
    "Match the rate to the mood or groove of the song, especially if the tremolo is very audible.",
    "Use lower depth if you want subtle movement without obvious level pumping.",
    "Try tempo sync for precise rhythmic effects, but use free timing when you want a more organic feel.",
    "Check whether a smoother or sharper waveform better suits the source.",
    "Be careful with heavy tremolo on important lead parts if it reduces clarity too much.",
  ];

  const relatedLinks = [
    {
      title: "Phaser",
      to: "/learning/phaser",
      description:
        "Learn how phaser adds spectral movement, while tremolo changes volume over time.",
    },
    {
      title: "Delay",
      to: "/learning/delay",
      description:
        "Explore how delay adds repeated echoes, which is very different from tremolo’s amplitude pulsing.",
    },
    {
      title: "Reverb",
      to: "/learning/reverb",
      description:
        "Understand how reverb adds space while tremolo adds direct rhythmic motion.",
    },
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "See how chorus adds width and shimmer, while tremolo adds level-based pulsing.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Tremolo | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Tremolo in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Tremolo is a modulation effect that changes volume over time. Instead of shifting
                      pitch or creating a delay-based sweep, tremolo simply makes a sound pulse, throb,
                      shimmer, or chop by moving its level up and down in a repeating pattern.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Because it is such a direct kind of modulation, tremolo can feel instantly musical.
                      It can be subtle and expressive on clean guitar, dreamy on pads and keys, or sharp
                      and rhythmic in more experimental production. It is one of the simplest modulation
                      ideas, but also one of the most versatile.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    What is tremolo?
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Tremolo is the repeated modulation of volume. When the effect is running, the
                    signal gets louder and quieter in a regular pattern. This can sound smooth and
                    wave-like or tight and choppy depending on the settings and waveform shape.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Tremolo is often confused with vibrato, but they are not the same. Tremolo changes
                    level. Vibrato changes pitch. This distinction matters because the ear experiences
                    the two effects very differently even though both create motion.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Tremolo has a strong connection to vintage guitar amplifiers, but it is just as
                    useful in modern production where it can create rhythmic texture and animation on
                    many kinds of sources.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    How tremolo works
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {tremoloConcepts.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Most tremolo effects use an LFO to control the volume movement. That LFO acts like
                    a repeating shape, and the audio follows it. The result can feel smooth and flowing
                    or rigid and mechanical depending on the selected shape and speed.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Common types of tremolo
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {tremoloTypes.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Tremolo vs vibrato
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Level movement vs pitch movement
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Tremolo changes amplitude, which means the volume pulses up and down. Vibrato
                        changes pitch, which means the note wobbles higher and lower. These two ideas
                        are often confused in guitar language, but in audio terms they describe
                        different effects.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        If you hear pulsing loudness, that is tremolo. If you hear pitch wobble, that
                        is vibrato.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Common tremolo controls
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    How tremolo is used in production
                  </Typography>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.35}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Tremolo can be a tone-shaping effect, a rhythmic effect, or both. It can sit
                    quietly in the background as subtle motion, or it can become one of the defining
                    musical features of a part.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Best practices for using tremolo
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MusicNoteRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Why tremolo remains so popular
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.35}>
                      {[
                        "It creates motion in a very direct and easy-to-hear way.",
                        "It can sound vintage, modern, subtle, hypnotic, or sharply rhythmic depending on the settings.",
                        "It works on many different sources, not just guitar.",
                        "It can add musical interest without changing pitch or adding obvious repeats.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <MusicNoteRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Final thoughts
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Tremolo is a simple idea with a lot of musical depth. By modulating level over
                    time, it can create softness, drama, groove, and motion without relying on pitch
                    changes or echoes.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Whether you want subtle shimmer or bold rhythmic chopping, tremolo remains one of
                    the most expressive and timeless modulation tools in music production.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}